import React, { useEffect, useState } from 'react';
import { AlertCircle, Camera, CheckCircle2, ExternalLink, Loader2, PackageCheck, UploadCloud } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { adminApi } from '../../context/AdminContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const absoluteUrl = (value) => {
  if (!value) return '';
  return value.startsWith('http') ? value : `${BACKEND_URL}${value}`;
};

const AdminCapture = () => {
  const [photo, setPhoto] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [priceMin, setPriceMin] = useState('0.010');
  const [stockQuantity, setStockQuantity] = useState('1');
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!photo) {
      setPreviewUrl('');
      return undefined;
    }
    const nextPreview = URL.createObjectURL(photo);
    setPreviewUrl(nextPreview);
    return () => URL.revokeObjectURL(nextPreview);
  }, [photo]);

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    setResult(null);
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!photo) return;
    setSubmitting(true);
    setError('');
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('photo', photo);
      formData.append('price_min', priceMin || '0');
      formData.append('stock_quantity', stockQuantity || '0');
      formData.append('is_active', isActive ? 'true' : 'false');
      const response = await adminApi.post('/staff/capture-product', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(response.data);
      setPhoto(null);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to identify and save the product';
      setError(typeof detail === 'string' ? detail : 'Failed to identify and save the product');
    } finally {
      setSubmitting(false);
    }
  };

  const identification = result?.identification || {};
  const product = result?.product;
  const confidence = Math.round((identification.confidence || 0) * 100);

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Installation Capture</h2>
          <p className="text-gray-600 mt-1">Create catalog products from finished Android screen installations</p>
        </div>
        <Badge variant="outline" className="w-fit">Staff Camera</Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-6">
        <Card>
          <CardContent className="p-5">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label>Installation Photo</Label>
                <label className="mt-2 flex min-h-[280px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 text-center hover:border-red-400 hover:bg-red-50/40 transition-colors overflow-hidden">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoChange}
                  />
                  {previewUrl ? (
                    <img src={previewUrl} alt="Installation preview" className="h-full max-h-[420px] w-full object-contain bg-black" />
                  ) : (
                    <div className="p-8 text-gray-600">
                      <Camera className="h-14 w-14 mx-auto mb-3 text-red-600" />
                      <div className="font-medium">Take or Choose Photo</div>
                      <div className="text-sm text-gray-500 mt-1">JPG, PNG, WebP up to 10MB</div>
                    </div>
                  )}
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label>Price Min (BHD)</Label>
                  <Input type="number" step="0.001" min="0" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} />
                </div>
                <div>
                  <Label>Stock</Label>
                  <Input type="number" min="0" value={stockQuantity} onChange={(e) => setStockQuantity(e.target.value)} />
                </div>
                <label className="flex items-end gap-2 rounded-md border px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="mb-1 h-4 w-4 accent-red-600"
                  />
                  Active product
                </label>
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={!photo || submitting}>
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <UploadCloud className="h-4 w-4 mr-2" />
                )}
                Identify and Save Product
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <PackageCheck className="h-5 w-5 text-red-600" />
              <h3 className="font-bold text-gray-900">Last Result</h3>
            </div>

            {result && product ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3 rounded-md border border-green-200 bg-green-50 p-3 text-green-800">
                  <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">{result.message}</div>
                    <div className="text-sm">{product.name?.en}</div>
                  </div>
                </div>

                <img src={absoluteUrl(result.image_url)} alt={product.name?.en || 'Captured product'} className="w-full rounded-md border bg-gray-50 object-contain max-h-72" />

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-gray-500">Brand</div>
                    <div className="font-medium">{identification.brand || '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Model</div>
                    <div className="font-medium">{identification.model || '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Years</div>
                    <div className="font-medium">{identification.year_range || '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Confidence</div>
                    <div className="font-medium">{confidence}%</div>
                  </div>
                </div>

                {identification.notes && (
                  <p className="rounded-md bg-gray-50 p-3 text-sm text-gray-600">{identification.notes}</p>
                )}

                <div className="flex flex-col sm:flex-row gap-2">
                  <Button asChild variant="outline" className="flex-1">
                    <Link to={`/shop/${product.id}`} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Product
                    </Link>
                  </Button>
                  <Button asChild className="flex-1 bg-red-600 hover:bg-red-700">
                    <Link to="/admin/products">Products</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-dashed py-16 text-center text-gray-500">
                <Camera className="h-10 w-10 mx-auto mb-3 text-gray-400" />
                <div>No capture saved yet</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminCapture;
